import React from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import type { VerificationStatus } from '../../types/practitioners';

/**
 * Feature flag: Set to true to enable verification UI
 * 
 * When enabled, this will show:
 * - ID Verification badges and status
 * - Certificate Verification badges and status
 * - Verification buttons and prompts
 * 
 * To re-enable: Change VERIFICATION_ENABLED to true
 */
const VERIFICATION_ENABLED = false;

interface VerificationStatusProps {
  type: 'id' | 'certificate';
  status?: VerificationStatus;
  verifiedAt?: string | null;
  className?: string;
}

export function VerificationStatusBadge({ 
  type, 
  status = 'pending', 
  verifiedAt,
  className = '' 
}: VerificationStatusProps) {
  // Hide verification UI if disabled
  if (!VERIFICATION_ENABLED) return null;
  const getStatusConfig = () => {
    switch (status) {
      case 'verified':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          text: type === 'id' ? 'ID Verified' : 'Certificate Verified',
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          text: type === 'id' ? 'ID Verification Failed' : 'Certificate Verification Failed',
        };
      case 'expired':
        return {
          icon: AlertCircle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          text: 'Verification Expired',
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          text: type === 'id' ? 'ID Verification Pending' : 'Certificate Verification Pending',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bgColor} ${config.borderColor} ${config.color} ${className}`}
      title={verifiedAt ? `Verified on ${new Date(verifiedAt).toLocaleDateString()}` : undefined}
    >
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium">{config.text}</span>
    </div>
  );
}

interface VerificationStatusListProps {
  idStatus?: VerificationStatus;
  certificateStatus?: VerificationStatus;
  idVerifiedAt?: string | null;
  certificateVerifiedAt?: string | null;
  className?: string;
}

export function VerificationStatusList({
  idStatus,
  certificateStatus,
  idVerifiedAt,
  certificateVerifiedAt,
  className = '',
}: VerificationStatusListProps) {
  // Hide verification UI if disabled
  if (!VERIFICATION_ENABLED) return null;
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {idStatus && (
        <VerificationStatusBadge
          type="id"
          status={idStatus}
          verifiedAt={idVerifiedAt}
        />
      )}
      {certificateStatus && (
        <VerificationStatusBadge
          type="certificate"
          status={certificateStatus}
          verifiedAt={certificateVerifiedAt}
        />
      )}
    </div>
  );
}


